import React, { useState, useEffect } from 'react';
import axiosInstance from '../../services/api/axiosInstance';

const MultiWebsiteSelector = ({ 
  selectedSiteIds = [], 
  onSiteChange, 
  teamId, 
  className = "",
  placeholder = "Select websites",
  showLabel = true,
  labelText = "Websites",
  selectFirstOnly = false // New prop to control auto-selection behavior
}) => {
  const [websites, setWebsites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);

  // Fetch websites for the team
  useEffect(() => {
    const fetchWebsites = async () => {
      if (!teamId) return;
      
      // Check sessionStorage cache first
      const cacheKey = `websites_${teamId}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
          try {
          const { data, timestamp } = JSON.parse(cached);
          // Use cache if less than 5 minutes old
          if (Date.now() - timestamp < 5 * 60 * 1000) {
            console.log('Using cached websites data for team:', teamId);
            // Filter to show only active/verified websites
            const activeWebsites = data.filter(w => w.is_verified || w.isVerified);
            setWebsites(activeWebsites);
            
            // Auto-select websites if none selected
            if (selectedSiteIds.length === 0 && activeWebsites.length > 0) {
              if (selectFirstOnly) {
                // Select only the first website
                onSiteChange([activeWebsites[0].site_id || activeWebsites[0].siteId]);
              } else {
                // Select all websites (legacy behavior)
                onSiteChange(activeWebsites.map(w => w.site_id || w.siteId));
              }
            }
            return;
          }
        } catch (e) {
          console.warn('Failed to parse cached websites:', e);
        }
      }
      
      try {
        setIsLoading(true);
        setError(null);
        
        let response;
        try {
          // Check if teamId is a PostgreSQL integer ID or team name
          const isNumericId = typeof teamId === 'number' || (!isNaN(teamId) && Number.isInteger(Number(teamId)));
          
          if (isNumericId) {
            // Use the /team/:teamId endpoint for integer IDs
            response = await axiosInstance.get(`/website/team/${teamId}`);
          } else {
            // Use the /name/:teamName endpoint for team names
            response = await axiosInstance.get(`/website/name/${encodeURIComponent(teamId)}`);
          }
        } catch (err) {
          // Fallback: use team name from localStorage if teamId was actually a name
          const teamName = localStorage.getItem('selectedTeam') || String(teamId);
          response = await axiosInstance.get(`/website/name/${encodeURIComponent(teamName)}`);
        }
        
        if (response.status === 200 && (response.data.websites || Array.isArray(response.data))) {
          const allWebsites = response.data.websites || response.data;
          // Filter to show only active/verified websites
          const activeWebsites = allWebsites.filter(w => w.is_verified || w.isVerified);
          setWebsites(activeWebsites);
          
          // Cache the result in sessionStorage (cache all websites, but display only active)
          sessionStorage.setItem(cacheKey, JSON.stringify({
            data: response.data.websites,
            timestamp: Date.now()
          }));
          
          // If no sites are selected and we have active websites, auto-select
          if (selectedSiteIds.length === 0 && activeWebsites.length > 0) {
            if (selectFirstOnly) {
              // Select only the first website
              onSiteChange([activeWebsites[0].site_id || activeWebsites[0].siteId]);
            } else {
              // Select all websites (legacy behavior)
              onSiteChange(activeWebsites.map(w => w.site_id || w.siteId));
            }
          }
        }
      } catch (err) {
        console.error('Error fetching websites:', err);
        setError('Failed to load websites');
      } finally {
        setIsLoading(false);
      }
    };

    fetchWebsites();
  }, [teamId]); // Removed selectedSiteIds and onSiteChange to prevent re-fetching

  // When parent clears selection (e.g. stale id from another team), auto-select first site so data can load
  useEffect(() => {
    if (selectedSiteIds.length !== 0 || websites.length === 0 || !onSiteChange) return;
    if (selectFirstOnly) {
      onSiteChange([websites[0].site_id || websites[0].siteId]);
    } else {
      onSiteChange(websites.map(w => w.site_id || w.siteId));
    }
  }, [selectedSiteIds.length, websites.length, selectFirstOnly, onSiteChange]);

  const selectedWebsites = websites.filter(w => selectedSiteIds.includes(w.site_id || w.siteId));

  const handleSiteToggle = (siteId) => {
    const newSelectedIds = selectedSiteIds.includes(siteId)
      ? selectedSiteIds.filter(id => id !== siteId)
      : [...selectedSiteIds, siteId];
    
    onSiteChange(newSelectedIds);
  };

  const handleSelectAll = () => {
    if (selectedSiteIds.length === websites.length) {
      // Deselect all
      onSiteChange([]);
    } else {
      // Select all
      onSiteChange(websites.map(w => w.site_id || w.siteId));
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const getDisplayText = () => {
    if (selectedWebsites.length === 0) {
      return placeholder;
    } else if (selectedWebsites.length === 1) {
      // Show only name, fallback to domain if name not available
      return selectedWebsites[0].name || selectedWebsites[0].Name || selectedWebsites[0].Domain || selectedWebsites[0].domain;
    } else if (selectedWebsites.length === websites.length) {
      return 'All websites';
    } else {
      return `${selectedWebsites.length} websites selected`;
    }
  };

  return (
    <div className={`relative ${className}`}>
      {showLabel && (
        <label className="block text-xs font-medium mb-2 text-[#475569]">
          {labelText}
        </label>
      )}
      
      <div className="relative">
        <button
          type="button"
          onClick={toggleDropdown}
          className="w-full px-3 py-2 text-sm bg-white/60 backdrop-blur-sm border border-gray-300 hover:bg-white/80 transition-all duration-200 flex items-center justify-between min-w-[180px] focus:outline-none focus:ring-2 focus:ring-[#1d0c46] focus:border-[#1d0c46]"
          disabled={isLoading}
        >
          <div className="flex items-center gap-2">
            {isLoading ? (
              <>
                <div className="w-2 h-2 bg-gray-400 animate-pulse"></div>
                <span className="text-[#475569]">Loading...</span>
              </>
            ) : (
              <span className="text-[#475569] truncate">
                {getDisplayText()}
              </span>
            )}
          </div>
          
          <svg 
            className={`w-4 h-4 text-[#475569] transition-transform duration-200 ml-auto ${isOpen ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-[9999] w-full mt-1 bg-white border border-gray-200 max-h-60 overflow-auto shadow-xl" style={{ top: '100%' }}>
            {error ? (
              <div className="px-3 py-2 text-sm text-red-600">
                {error}
              </div>
            ) : websites.length === 0 ? (
              <div className="px-3 py-2 text-sm text-[#475569]">
                No websites found
              </div>
            ) : (
              <>
                {/* Select All option */}
                <button
                  onClick={handleSelectAll}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 border-b border-gray-100 ${
                    selectedSiteIds.length === websites.length ? 'bg-[#1d0c46]/10 text-[#1d0c46]' : 'text-[#475569]'
                  }`}
                >
                  <div className={`w-4 h-4 border-2 flex items-center justify-center ${
                    selectedSiteIds.length === websites.length ? 'bg-[#1d0c46] border-[#1d0c46]' : 'border-gray-300'
                  }`}>
                    {selectedSiteIds.length === websites.length && (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <span className="font-medium">Select All</span>
                </button>

                {/* Individual website options */}
                {websites.map((website) => (
                  <button
                    key={website.site_id || website.siteId}
                    onClick={() => handleSiteToggle(website.site_id || website.siteId)}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                      selectedSiteIds.includes(website.site_id || website.siteId) ? 'bg-[#1d0c46]/10 text-[#1d0c46]' : 'text-[#475569]'
                    }`}
                  >
                    <div className={`w-4 h-4 border-2 flex items-center justify-center ${
                      selectedSiteIds.includes(website.site_id || website.siteId) ? 'bg-[#1d0c46] border-[#1d0c46]' : 'border-gray-300'
                    }`}>
                      {selectedSiteIds.includes(website.site_id || website.siteId) && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span className="truncate">{website.name || website.Name || website.domain || website.Domain}</span>
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* Click outside to close */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-[9998]" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default MultiWebsiteSelector;
